import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  Stack,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useMemo, useRef, useState } from 'react';
import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { readViteEnv } from '../lib/readViteEnv';
import {
  employerVerificationDashboardApi,
  type EmployerAutocompleteSuggestionDto,
} from '../api/employerVerificationDashboardApi';

function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<T>) => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

type Props = {
  lat: number;
  lng: number;
  title: string;
  formattedAddress?: string | null;
};

export function CompanyVerificationMap({ lat, lng, title, formattedAddress }: Props) {
  const mapRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'idle' | 'loading' | 'ready' | 'missing_key' | 'error'>('idle');
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const [searchMarker, setSearchMarker] = useState<google.maps.marker.AdvancedMarkerElement | null>(null);

  const [options, setOptionsList] = useState<EmployerAutocompleteSuggestionDto[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState<EmployerAutocompleteSuggestionDto | null>(null);

  const fetchSuggestions = useMemo(
    () =>
      debounce(async (query: string) => {
        if (query.length < 2) {
          setOptionsList([]);
          return;
        }
        setLoadingSearch(true);
        try {
          const results = await employerVerificationDashboardApi.autocomplete(query);
          setOptionsList(results || []);
        } catch {
          setOptionsList([]);
        } finally {
          setLoadingSearch(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    fetchSuggestions(inputValue);
  }, [inputValue, fetchSuggestions]);

  useEffect(() => {
    const apiKey = readViteEnv('VITE_GOOGLE_MAPS_API_KEY') ?? '';
    if (!apiKey) {
      setPhase('missing_key');
      return;
    }

    let cancelled = false;
    setPhase('loading');

    void (async () => {
      try {
        const mapIdRaw = readViteEnv('VITE_GOOGLE_MAPS_MAP_ID') ?? '';
        const mapId = mapIdRaw || 'DEMO_MAP_ID';

        const authPolicy = readViteEnv('VITE_GOOGLE_MAPS_AUTH_REFERRER_POLICY') ?? '';

        setOptions({
          key: apiKey,
          v: 'weekly',
          ...(authPolicy ? { authReferrerPolicy: authPolicy } : {}),
        });

        const { Map } = await importLibrary('maps');
        const { AdvancedMarkerElement } = await importLibrary('marker');

        if (cancelled || !mapRef.current) return;

        const map = new Map(mapRef.current, {
          center: { lat, lng },
          zoom: 15,
          mapTypeControl: true,
          fullscreenControl: true,
          streetViewControl: true,
          mapId,
        });

        new AdvancedMarkerElement({
          map,
          position: { lat, lng },
          title: `Verified: ${title}`,
        });

        setMapInstance(map);
        setPhase('ready');
      } catch {
        if (!cancelled) setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lat, lng, title]);

  const handlePlaceSelect = async (place: EmployerAutocompleteSuggestionDto | null) => {
    setSelectedPlace(place);
    if (!place || !mapInstance) {
      clearSearch();
      return;
    }

    try {
      setLoadingSearch(true);
      const details = await employerVerificationDashboardApi.placeDetails(place.placeId);

      const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker')) as google.maps.MarkerLibrary;

      setSearchMarker((prev) => {
        if (prev) prev.map = null;
        return null;
      });

      const newMarker = new AdvancedMarkerElement({
        map: mapInstance,
        position: { lat: details.latitude, lng: details.longitude },
        title: `Search Result: ${details.name}`,
        content: (() => {
          const pin = document.createElement('div');
          pin.style.width = '24px';
          pin.style.height = '24px';
          pin.style.borderRadius = '50%';
          pin.style.backgroundColor = '#1976d2';
          pin.style.border = '3px solid white';
          pin.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
          return pin;
        })(),
      });

      setSearchMarker(newMarker);

      const bounds = new google.maps.LatLngBounds();
      bounds.extend({ lat, lng });
      bounds.extend({ lat: details.latitude, lng: details.longitude });
      mapInstance.fitBounds(bounds, 50);
    } finally {
      setLoadingSearch(false);
    }
  };

  const clearSearch = () => {
    setInputValue('');
    setSelectedPlace(null);
    if (searchMarker) {
      searchMarker.map = null;
      setSearchMarker(null);
    }
    if (mapInstance) {
      mapInstance.setCenter({ lat, lng });
      mapInstance.setZoom(15);
    }
  };

  if (phase === 'missing_key') {
    const q = encodeURIComponent(formattedAddress || `${lat},${lng}`);
    return (
      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          The map needs a browser key at dev time. Check your <code>.env</code> settings.
        </Typography>
        <Button
          variant="contained"
          size="small"
          href={`https://www.google.com/maps/search/?api=1&query=${q}`}
          target="_blank"
          rel="noreferrer"
        >
          Open in Google Maps (new tab)
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
      {phase === 'ready' && (
        <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 1 }}>
          <Autocomplete
            fullWidth
            size="small"
            options={options}
            getOptionLabel={(option) => option.fullText}
            filterOptions={(x) => x}
            loading={loadingSearch}
            value={selectedPlace}
            onChange={(_, newValue) => void handlePlaceSelect(newValue)}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search company or address to cross-verify…"
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { border: 'none' },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {loadingSearch ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.placeId}>
                <Stack>
                  <Typography variant="body2" fontWeight="medium">
                    {option.mainText}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.secondaryText}
                  </Typography>
                </Stack>
              </li>
            )}
          />
        </Box>
      )}
      <Box ref={mapRef} sx={{ width: '100%', height: 400, bgcolor: 'action.hover' }} />
      {phase !== 'ready' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'action.hover',
            pointerEvents: 'none',
          }}
        >
          <Stack alignItems="center" spacing={1}>
            {phase === 'loading' && <Typography variant="caption">Loading engine…</Typography>}
            <Typography variant="body2" color="text.secondary">
              {phase === 'error' ? 'Map failed to load.' : 'Initializing Maps…'}
            </Typography>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
